import { useCallback } from 'react'
import { Ellipsis, ListPlus, Plus, Trash2 } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Input } from 'src/shared/components/ui/input'
import { Separator } from 'src/shared/components/ui/separator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOrderTableCurrency } from '../../../../currency'
import { useQuoteSubItemApi } from 'src/api/db/apiQuoteSubItem'
import { ChangeDecorationMenuItems } from 'src/pages/_myAccount/EditDecoration/components/ChangeDecorationDialog'
import { useDebouncedFieldUpdate } from '../../../../hooks'
import type { ProductReceiptLine } from '../../../../utils'

const fieldWidth = (value: string | number, min = 2, max = 12) => {
  const len = String(value).length
  return `${Math.min(max, Math.max(min, len + 1))}ch`
}

const EditableSubItemLine = ({
  line,
  currency,
  onDelete,
  onEditDecoration,
  onChangeDecoration,
}: {
  line: ProductReceiptLine
  currency: string
  onDelete?: (subItemId: number) => void
  onEditDecoration?: (subItemId: number) => void
  onChangeDecoration?: (subItemId: number) => void
}) => {
  const { updateQuoteSubItem } = useQuoteSubItemApi()
  const subItemId = line.subItemId!

  const updateDescription = useCallback(
    (value: string) =>
      updateQuoteSubItem.mutate({ id: subItemId, description: value }),
    [subItemId, updateQuoteSubItem]
  )
  const updateUnitPrice = useCallback(
    (value: number) =>
      updateQuoteSubItem.mutate({ id: subItemId, unit_price: value }),
    [subItemId, updateQuoteSubItem]
  )
  const updateQuantity = useCallback(
    (value: number) =>
      updateQuoteSubItem.mutate({ id: subItemId, quantity: value }),
    [subItemId, updateQuoteSubItem]
  )

  const [labelLocal, , handleLabelChange] = useDebouncedFieldUpdate(
    line.label,
    updateDescription
  )
  const [unitPriceLocal, , handleUnitPriceChange] = useDebouncedFieldUpdate(
    line.unitPrice,
    updateUnitPrice
  )
  const [quantityLocal, , handleQuantityChange] = useDebouncedFieldUpdate(
    line.quantity,
    updateQuantity
  )

  const amount = Math.max(0, unitPriceLocal * quantityLocal)

  return (
    <div className="contents group/line">
      <Row className="min-w-0 items-baseline gap-1 overflow-hidden">
        <Input
          variant="plain"
          value={labelLocal}
          onChange={(e) => handleLabelChange(e.target.value)}
          className="min-w-0 max-w-[200px] shrink truncate text-xs text-muted-foreground"
        />
        {line.showDetail ? (
          <span className="shrink-0 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground/70">
            <Input
              variant="plain"
              type="number"
              value={unitPriceLocal}
              onChange={(e) =>
                handleUnitPriceChange(Number(e.target.value) || 0)
              }
              className="inline max-w-[4rem] text-[11px] tabular-nums text-muted-foreground/70 align-baseline"
              style={{ width: fieldWidth(unitPriceLocal, 2, 5) }}
            />
            {' × '}
            <Input
              variant="plain"
              type="number"
              value={quantityLocal}
              onChange={(e) =>
                handleQuantityChange(Number(e.target.value) || 0)
              }
              className="inline max-w-[3rem] text-[11px] tabular-nums text-muted-foreground/70 align-baseline"
              style={{ width: fieldWidth(quantityLocal, 1, 4) }}
            />
          </span>
        ) : null}
      </Row>
      <Row className="shrink-0 items-center justify-end gap-0.5">
        {line.showDetail ? (
          <span className="whitespace-nowrap text-right text-xs tabular-nums text-foreground">
            {amount.toLocaleString('sv-SE')} {currency}
          </span>
        ) : (
          <span className="whitespace-nowrap text-right text-xs tabular-nums text-foreground">
            <Input
              variant="plain"
              type="number"
              value={unitPriceLocal}
              onChange={(e) =>
                handleUnitPriceChange(Number(e.target.value) || 0)
              }
              className="inline max-w-[4.5rem] text-right text-xs tabular-nums text-foreground align-baseline"
              style={{ width: fieldWidth(unitPriceLocal, 3, 6) }}
            />{' '}
            {currency}
          </span>
        )}
        {line.kind === 'decoration' &&
        (onEditDecoration || onChangeDecoration) &&
        line.subItemId ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded p-0.5 opacity-0 transition-opacity hover:bg-muted group-hover/line:opacity-100"
                aria-label="Decoration actions"
              >
                <Ellipsis className="h-3 w-3 text-gray-400 hover:text-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <ChangeDecorationMenuItems
                onManualDesign={() => onEditDecoration?.(line.subItemId!)}
                onChangeDecoration={() => onChangeDecoration?.(line.subItemId!)}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {onDelete && line.subItemId ? (
          <button
            type="button"
            onClick={() => onDelete(line.subItemId!)}
            className="rounded p-0.5 opacity-0 transition-opacity hover:bg-red-50 group-hover/line:opacity-100"
          >
            <Trash2 className="h-3 w-3 text-gray-400 hover:text-red-500" />
          </button>
        ) : null}
      </Row>
    </div>
  )
}

const ViewReceiptLine = ({
  line,
  currency,
  onEditDecoration,
  onChangeDecoration,
}: {
  line: ProductReceiptLine
  currency: string
  onEditDecoration?: (subItemId: number) => void
  onChangeDecoration?: (subItemId: number) => void
}) => {
  const { t } = useLocalization()
  const detail = line.showDetail
    ? `${line.unitPrice.toLocaleString('sv-SE')} × ${line.quantity}`
    : undefined
  const canEditDecoration =
    line.kind === 'decoration' &&
    line.subItemId &&
    (onEditDecoration || onChangeDecoration)

  return (
    <div className="contents">
      {canEditDecoration ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="group/label min-w-0 text-left text-xs text-muted-foreground hover:text-foreground"
            >
              {t(line.label)}
              {detail ? (
                <span className="ml-1 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground/70">
                  {detail}
                </span>
              ) : null}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <ChangeDecorationMenuItems
              onManualDesign={() => onEditDecoration?.(line.subItemId!)}
              onChangeDecoration={() => onChangeDecoration?.(line.subItemId!)}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span className="text-xs text-muted-foreground">
          {t(line.label)}
          {detail ? (
            <span className="ml-1 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground/70">
              {detail}
            </span>
          ) : null}
        </span>
      )}
      <span className="whitespace-nowrap text-right text-xs tabular-nums text-foreground">
        {line.amount.toLocaleString('sv-SE')} {currency}
      </span>
    </div>
  )
}

export const ProductRowPriceSummary = ({
  lines,
  total,
  isEditMode = false,
  productUnitPrice,
  onProductUnitPriceChange,
  discount = 0,
  onDiscountChange,
  onDeleteSubItem,
  onEditDecoration,
  onChangeDecoration,
  onAddDecoration,
  onAddCustomLine,
  canAddCustomLine = false,
}: {
  lines: ProductReceiptLine[]
  total: number
  isEditMode?: boolean
  productUnitPrice?: number
  onProductUnitPriceChange?: (value: number) => void
  discount?: number
  onDiscountChange?: (value: number) => void
  onDeleteSubItem?: (subItemId: number) => void
  onEditDecoration?: (subItemId: number) => void
  onChangeDecoration?: (subItemId: number) => void
  onAddDecoration?: () => void
  onAddCustomLine?: () => void
  canAddCustomLine?: boolean
}) => {
  const { t } = useLocalization()
  const currency = useOrderTableCurrency()

  return (
    <Column className="w-full gap-0 text-left">
      <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-1">
        {lines.map((line) => {
          if (!isEditMode) {
            return (
              <ViewReceiptLine
                key={line.key}
                line={line}
                currency={currency}
                onEditDecoration={onEditDecoration}
                onChangeDecoration={onChangeDecoration}
              />
            )
          }

          if (
            line.kind === 'product' &&
            onProductUnitPriceChange &&
            productUnitPrice !== undefined
          ) {
            const amount = Math.max(0, productUnitPrice * line.quantity)
            return (
              <div key={line.key} className="contents">
                <Row className="min-w-0 items-baseline gap-1 overflow-hidden">
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {line.label}
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground/70">
                    <Input
                      variant="plain"
                      type="number"
                      value={productUnitPrice}
                      onChange={(e) =>
                        onProductUnitPriceChange(Number(e.target.value) || 0)
                      }
                      className="inline max-w-[4rem] text-[11px] tabular-nums text-muted-foreground/70 align-baseline"
                      style={{ width: fieldWidth(productUnitPrice, 2, 5) }}
                    />
                    {` × ${line.quantity}`}
                  </span>
                </Row>
                <span className="whitespace-nowrap text-right text-xs tabular-nums text-foreground">
                  {amount.toLocaleString('sv-SE')} {currency}
                </span>
              </div>
            )
          }

          if (line.subItemId) {
            return (
              <EditableSubItemLine
                key={line.key}
                line={line}
                currency={currency}
                onDelete={onDeleteSubItem}
                onEditDecoration={onEditDecoration}
                onChangeDecoration={onChangeDecoration}
              />
            )
          }

          return (
            <ViewReceiptLine
              key={line.key}
              line={line}
              currency={currency}
              onEditDecoration={onEditDecoration}
              onChangeDecoration={onChangeDecoration}
            />
          )
        })}

        {isEditMode && onDiscountChange && discount > 0 ? (
          <>
            <span className="text-xs text-muted-foreground">
              {t('Discount', 'Rabatt')}
            </span>
            <span className="whitespace-nowrap text-right text-xs tabular-nums text-foreground">
              −
              <Input
                variant="plain"
                type="number"
                value={discount}
                onChange={(e) => onDiscountChange(Number(e.target.value) || 0)}
                className="inline max-w-[4rem] text-right text-xs tabular-nums text-foreground align-baseline"
                style={{ width: fieldWidth(discount, 2, 5) }}
              />{' '}
              {currency}
            </span>
          </>
        ) : null}
      </div>

      <Separator className="my-1.5" />

      <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4">
        <span className="text-sm font-medium text-foreground">
          {t('Total', 'Totalt')}
        </span>
        <span className="whitespace-nowrap text-right text-sm font-semibold tabular-nums text-foreground">
          {total.toLocaleString('sv-SE')} {currency}
        </span>
      </div>

      {isEditMode && (onAddDecoration || onAddCustomLine) ? (
        <Row className="mt-2 flex-wrap items-center gap-x-3 gap-y-1">
          {onAddDecoration ? (
            <button
              type="button"
              onClick={onAddDecoration}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-3 w-3" />
              {t('Add decoration', 'Lägg till dekoration')}
            </button>
          ) : null}
          {canAddCustomLine && onAddCustomLine ? (
            <button
              type="button"
              onClick={onAddCustomLine}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ListPlus className="h-3 w-3" />
              {t('Add line', 'Lägg till rad')}
            </button>
          ) : null}
        </Row>
      ) : null}
    </Column>
  )
}
